import app from 'flarum/forum/app';
import IndexPage from 'flarum/forum/components/IndexPage';
import Page from 'flarum/common/components/Page';
import ItemList from 'flarum/common/utils/ItemList';
import extractText from 'flarum/common/utils/extractText';
import Button from 'flarum/common/components/Button';
import Select from 'flarum/common/components/Select';
import LogInModal from 'flarum/forum/components/LogInModal';
import SignUpModal from 'flarum/forum/components/SignUpModal';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import { cameras, lines, matchesCamera, lineColor, load, loaded } from '../cameraData';
import { isStaff } from '../downloadCounts';
import { cameraCard } from './CameraCard';
import CameraHelpModal from './CameraHelpModal';
import CameraIdentifyBar from './CameraIdentifyBar';
import CameraStatsModal from './CameraStatsModal';

/**
 * The camera index: all product lines, card or list view, search and a
 * state filter, plus the permanent "Camera not listed?" escape hatch.
 *
 * It extends IndexPage rather than Page so it lands in the discussion-list
 * pane with the side nav beside it, instead of taking the full window width.
 * Only contentItems() is replaced; the hero, the container/sideNav markup and
 * the sidebar (including its "Start a Discussion" button) come from core.
 */
// Safari "Block All Cookies" and some webviews throw on any localStorage access
const store = {
  get: (k) => { try { return localStorage.getItem(k); } catch (e) { return null; } },
  set: (k, v) => { try { localStorage.setItem(k, v); } catch (e) {} },
};

export default class CamerasIndexPage extends IndexPage {
  oninit(vnode) {
    // NOT super.oninit(): IndexPage.oninit calls app.discussions.refreshParams(),
    // a full GET /api/discussions this page never renders (one Flarum boot per
    // cold hit, 2026-08-23), and files this url in app.history as 'index'.
    // Page.oninit plus the two layout fields is everything the view needs.
    Page.prototype.oninit.call(this, vnode);
    this.bodyClass = 'App--index';
    this.scrollTopOnCreate = false;
    app.history.push('cameras', extractText(app.translator.trans('acseven-chdk-cameras.forum.index.title')));

    this.q = '';
    // NOT this.state (Mithril territory) and NOT this.view -- that one is the
    // component's own render method, and assigning a string over it makes
    // Mithril call a string ("this.apply is not a function"). The localStorage
    // key keeps its old name.
    this.filter = 'all';
    // list by default: no model has a real photo yet, so the card grid is a
    // wall of coloured letter plates and the list says more per screen.
    this.layout = store.get('chdk-cameras.view') || 'list';

    // Guests never get past gate(), so they never pay for the fetch.
    if (app.session.user) load().then(() => m.redraw());
  }

  setTitle() {
    app.setTitle(extractText(app.translator.trans('acseven-chdk-cameras.forum.index.title')));
    app.setTitleCount(0);
  }

  contentItems() {
    const items = new ItemList();

    // Members only. The sidebar button stays visible for guests on purpose --
    // it is the funnel; it just lands here on the sign-up wall instead of the
    // index. ponytail: a client-side gate, not access control. The camera data
    // ships inside forum.js, which anyone can read, so this shapes the funnel,
    // it does not keep the data secret.
    items.add('cameras', app.session.user ? this.cameraContent() : this.gate());

    return items;
  }

  gate() {
    // Registration is a forum-wide switch (Admin -> Basics). With it off there
    // is nothing to offer a guest but the login, so that one becomes primary
    // and the copy stops promising a sign-up that cannot happen.
    const canSignUp = app.forum.attribute('allowSignUp');

    return (
      <div className="CamerasIndexPage CamerasGate">
        <div className="CamerasGate-icon">
          <i className="fas fa-camera" />
        </div>
        <h2 className="CamerasGate-title">{app.translator.trans('acseven-chdk-cameras.forum.gate.title')}</h2>
        <p className="CamerasGate-body">
          {app.translator.trans('acseven-chdk-cameras.forum.gate.' + (canSignUp ? 'body' : 'body_no_sign_up'))}
        </p>
        <div className="CamerasGate-actions">
          {canSignUp && (
            <Button
              className="Button Button--primary CamerasGate-action"
              icon="fas fa-user-plus"
              onclick={() => app.modal.show(SignUpModal)}
            >
              {app.translator.trans('acseven-chdk-cameras.forum.gate.sign_up')}
            </Button>
          )}
          <Button
            className={'Button CamerasGate-action' + (canSignUp ? '' : ' Button--primary')}
            icon="fas fa-sign-in-alt"
            onclick={() => app.modal.show(LogInModal)}
          >
            {app.translator.trans('acseven-chdk-cameras.forum.gate.log_in')}
          </Button>
        </div>
      </div>
    );
  }

  cameraContent() {
    if (!loaded) return <LoadingIndicator className="CamerasIndexPage-loading" />;

    const filtered = this.filteredCameras();

    return (
      <div className="CamerasIndexPage">
        {/* Above the search row: most owners know the name on the front of the
            camera but not the firmware version, and the version is what picks
            a build. A photo the camera took carries both. */}
        <CameraIdentifyBar />

        <div className="IndexPage-toolbar CamerasIndexPage-toolbar">
          <div className="IndexPage-toolbar-view">
            <input
              className="FormControl CamerasIndexPage-search"
              type="search"
              placeholder={app.translator.trans('acseven-chdk-cameras.forum.index.search_placeholder')}
              value={this.q}
              oninput={(e) => {
                this.q = e.target.value;
              }}
            />
            {Select.component({
              className: 'CamerasIndexPage-state',
              value: this.filter,
              onchange: (v) => (this.filter = v),
              options: {
                all: app.translator.trans('acseven-chdk-cameras.forum.index.filter_all'),
                stable: app.translator.trans('acseven-chdk-cameras.forum.index.filter_stable'),
                alpha: app.translator.trans('acseven-chdk-cameras.forum.index.filter_new'),
              },
            })}
            <Button
              className="Button Button--icon CamerasIndexPage-viewToggle"
              icon={this.layout === 'cards' ? 'fas fa-list' : 'fas fa-th-large'}
              title={app.translator.trans('acseven-chdk-cameras.forum.index.toggle_view')}
              onclick={() => {
                this.layout = this.layout === 'cards' ? 'list' : 'cards';
                store.set('chdk-cameras.view', this.layout);
              }}
            />
            <Button
              className="Button CamerasIndexPage-notListed"
              icon="fas fa-question-circle"
              onclick={() => app.modal.show(CameraHelpModal)}
            >
              {app.translator.trans('acseven-chdk-cameras.forum.index.not_listed')}
            </Button>
            {isStaff() && (
              <Button
                className="Button CamerasIndexPage-stats"
                icon="fas fa-chart-bar"
                onclick={() => app.modal.show(CameraStatsModal)}
              >
                {app.translator.trans('acseven-chdk-cameras.forum.stats.button')}
              </Button>
            )}
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="CamerasIndexPage-empty">
            <p>{app.translator.trans('acseven-chdk-cameras.forum.index.no_results')}</p>
            <Button className="Button Button--primary" onclick={() => app.modal.show(CameraHelpModal)}>
              {app.translator.trans('acseven-chdk-cameras.forum.index.not_listed')}
            </Button>
          </div>
        ) : (
          lines.map((line) => {
            const group = filtered.filter((c) => c.line === line);
            if (!group.length) return null;

            return (
              <section className="CamerasIndexPage-group">
                <h3 className="Dropdown-header">
                  {line} <span className="CamerasIndexPage-groupCount">({group.length})</span>
                </h3>
                {this.layout === 'cards' ? (
                  <div className="CamerasGrid">
                    {group.map((camera) => (
                      <a
                        className="CamerasGrid-item"
                        href={app.route('camera', { id: camera.id })}
                        style={{ '--camera-line': lineColor(line) }}
                      >
                        {cameraCard(camera)}
                      </a>
                    ))}
                  </div>
                ) : (
                  <ul className="CamerasList">
                    {group.map((camera) => (
                      <li className="CamerasList-item">
                        <a href={app.route('camera', { id: camera.id })}>
                          <span className="CamerasList-line" style={{ background: lineColor(line) }}>
                            {camera.line}
                          </span>
                          <span className="CamerasList-name">{camera.name}</span>
                          {camera.aka.length > 0 && <span className="CamerasList-aka">{camera.aka.join(' · ')}</span>}
                          {camera.state === 'alpha' && (
                            <span className="CameraCard-badge CameraCard-badge--alpha">
                              {app.translator.trans('acseven-chdk-cameras.forum.badge.new_port')}
                            </span>
                          )}
                          <span className="CamerasList-count">{camera.firmware.length}</span>
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            );
          })
        )}
      </div>
    );
  }

  filteredCameras() {
    return cameras.filter((c) => {
      if (this.filter === 'stable' && c.state !== 'stable') return false;
      if (this.filter === 'alpha' && c.state !== 'alpha') return false;
      return matchesCamera(c, this.q);
    });
  }
}
